/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
  forwardRef,
} from 'react';
import { View, useWindowDimensions, ScrollView } from 'react-native';
import ButtonView, {
  ButtonViewType,
} from '@airasia-phoenix/react-native/components/ButtonView';
import { pd } from '@airasia-phoenix/react-native/utils';
import { StyleSheet } from 'react-native-unistyles';
import { useTranslation } from 'react-i18next';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';
import { LoadingStateView } from '../../components/LoadingStateView';
import { EmptyStateView } from '../../components/EmptyStateView';
import { MovementsGridSkeletonView } from '../../components/MovementsGridSkeletonView';
import { FeedListWidget } from '../../widgets/FeedListWidget';
import { FooterView } from '../../components/FooterView';
import { SocialFeedAnimationView } from '../../components/SocialFeedAnimationView';
import { useSocialFeed } from '../../hooks/useSocialFeed';
import { useSocialFeedPageContext } from '../../context/SocialFeedPageContext';
import { useSocialAnalytics } from '../../analytics';
import type {
  Post,
  SocialFeedData,
  SocialFeedPageProps,
  SocialFeedState,
} from './types';
import { URLS } from '../../constants/urls';
import { useAppConfig } from '@airasia-rn/ui';

type ApiErrorShape = {
  response?: { status?: number; data?: { message?: string } };
  request?: unknown;
  message?: string;
};

const parseApiError = (
  error: unknown,
): { code: string; message: string; description: string } => {
  const err = error as ApiErrorShape;
  if (err?.response) {
    const code = err.response.status?.toString() ?? 'API_ERROR';
    const message = err.message ?? 'API request failed';
    return {
      code,
      message,
      description: err.response.data?.message ?? message,
    };
  }
  if (err?.request) {
    const message = err.message ?? 'Network error';
    return { code: 'NETWORK_ERROR', message, description: message };
  }
  if (error instanceof Error) {
    return {
      code: 'API_ERROR',
      message: error.message,
      description: error.message,
    };
  }
  return {
    code: 'API_ERROR',
    message: 'Unknown error',
    description: 'Unknown error',
  };
};

// Offset from top of the screen for the animation overlay (accounts for header/tab height)
const ANIMATION_OVERLAY_TOP_OFFSET = 80;
// When the host renders the chips row (showCategoryChips false), the page no
// longer has its own 64px chips block, so the overlay keeps the same effective
// position with a smaller offset.
const ANIMATION_OVERLAY_TOP_OFFSET_NO_CHIPS = 16;
// Feed reveal starts after 500ms delay once Phase 2 begins, slides up and fades in
const FEED_REVEAL_DELAY_MS = 500;
const FEED_REVEAL_DURATION_MS = 1000;
const BOTTOM_EASING = Easing.out(Easing.cubic);
const FEED_REVEAL_TIMING = {
  duration: FEED_REVEAL_DURATION_MS,
  easing: BOTTOM_EASING,
};

type SocialFeedPageViewProps = SocialFeedPageProps & {
  state: SocialFeedState;
  data?: SocialFeedData | null;
  runFeedAnimation?: boolean;
  animationDone?: boolean;
  onAnimationEnd?: () => void;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  hasNextPage?: boolean;
};

const handleRetry = (onRetry?: () => void) => () => {
  onRetry?.();
};

const SocialFeedPageView = ({
  state,
  data,
  runFeedAnimation = false,
  animationDone = false,
  onAnimationEnd,
  onFeedItemClick,
  onFooterChipClick,
  onRetry,
  onEmptyState,
  onNoInternetState,
  onResultState,
  onLoadingState,
  onLoadMore,
  isLoadingMore,
  hasNextPage,
  showCategoryChips = true,
}: SocialFeedPageViewProps) => {
  const { t } = useTranslation('social');
  const { height: screenHeight } = useWindowDimensions();

  const emptyData: SocialFeedData = {
    data: [],
    categories: [],
    page: 1,
    pageSize: 20,
    totalRecords: 0,
  };

  const feedData = data || emptyData;
  const categories = feedData.categories || [];
  const items = feedData.data || [];
  const hasChips = categories.length > 0;
  const hasData = items.length > 0;

  const showAnimation = runFeedAnimation && !animationDone && hasData;

  const animationImages = useMemo(() => {
    if (!showAnimation) return undefined;
    return [
      items[0]?.image || undefined,
      items[1]?.image || undefined,
      items[2]?.image || undefined,
    ] as [string?, string?, string?];
  }, [showAnimation, items]);

  // Feed container — starts off-screen below, slides up while invisible, then appears
  const bottomTranslateY = useSharedValue(screenHeight);
  const bottomOpacity = useSharedValue(0);

  const handlePhaseTwo = useCallback(() => {
    // Feed slides up from bottom after delay (synced with Phase 2)
    bottomTranslateY.value = withDelay(
      FEED_REVEAL_DELAY_MS,
      withTiming(0, FEED_REVEAL_TIMING),
    );
    // Instantly show the feed once it has fully moved into position
    bottomOpacity.value = withDelay(
      FEED_REVEAL_DELAY_MS + FEED_REVEAL_DURATION_MS,
      withTiming(1, { duration: 0 }),
    );
  }, [bottomTranslateY, bottomOpacity]);

  const bottomAnimatedStyle = useAnimatedStyle(
    () => ({
      opacity: bottomOpacity.value,
      transform: [{ translateY: bottomTranslateY.value }],
    }),
    [bottomOpacity, bottomTranslateY],
  );

  useEffect(() => {
    if (state === 'loading') {
      onLoadingState?.();
    }
  }, [state, onLoadingState]);

  useEffect(() => {
    if (state === 'noInternet') {
      onNoInternetState?.();
    }
  }, [state, onNoInternetState]);

  useEffect(() => {
    if (state === 'result' && !hasChips && !hasData) {
      onEmptyState?.();
    }
  }, [state, hasChips, hasData, onEmptyState]);

  useEffect(() => {
    if ((state === 'result' || state === 'empty') && (hasChips || hasData)) {
      onResultState?.(feedData);
    }
  }, [state, hasChips, hasData, onResultState, feedData]);

  const renderLoadingState = () => (
    <View style={{ marginTop: hasChips ? 64 : 0 }}>
      <MovementsGridSkeletonView />
    </View>
  );

  const renderErrorState = () => (
    <EmptyStateView
      imageUrl={URLS.API_ERROR}
      title={t('movement.errorTitle')}
      description={t('movement.errorDescription')}
      cta={t('movement.buttonRetry')}
      ctaClicked={handleRetry(onRetry)}
    />
  );

  const renderEmptyState = () => (
    <EmptyStateView
      imageUrl={URLS.NO_RESULT_FOUND}
      title={t('movement.emptyResult')}
    />
  );

  const renderNoInternetState = () => (
    <EmptyStateView
      image={require('../../assets/images/img_no_internet.png')}
      title={t('movement.noInternetTitle')}
      description={t('movement.noInternetDescription')}
      cta={t('movement.buttonRetry')}
      ctaClicked={handleRetry(onRetry)}
    />
  );

  const renderResultState = () => {
    if (!hasData && !hasChips) {
      return renderEmptyState();
    }

    return (
      <View style={styles.animationWrapper}>
        {showCategoryChips && hasChips && (
          <View style={styles.chipsContainer}>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.chipsContent}
            >
              {categories.map((cat, index) => {
                // If deepLink is empty, it means this is the "All" or default chip showing the grid.
                const isSelected = !cat.deepLink;
                return (
                  <View
                    key={cat.key || String(index)}
                    style={styles.chipWrapper}
                  >
                    <ButtonView
                      type={
                        isSelected
                          ? ButtonViewType.PRIMARY_SMALL
                          : ButtonViewType.SECONDARY_SMALL
                      }
                      text={cat.title}
                      onPress={() => {
                        if (cat.deepLink) {
                          onFooterChipClick?.(
                            cat.deepLink,
                            {
                              location_id: '',
                              city: '',
                              country: '',
                              country_code: '',
                            },
                            cat,
                            index,
                          );
                        }
                      }}
                    />
                  </View>
                );
              })}
            </ScrollView>
          </View>
        )}
        <Animated.View
          style={[
            styles.feedWrapper,
            showAnimation ? bottomAnimatedStyle : undefined,
          ]}
        >
          <FeedListWidget
            items={feedData.data}
            onFeedItemClick={onFeedItemClick}
            onLoadMore={onLoadMore}
            isLoadingMore={isLoadingMore}
            hasNextPage={hasNextPage}
          />
        </Animated.View>

        {showAnimation && animationImages && (
          <Animated.View
            style={[
              styles.animationOverlay,
              !showCategoryChips && {
                top: ANIMATION_OVERLAY_TOP_OFFSET_NO_CHIPS,
              },
            ]}
          >
            <SocialFeedAnimationView
              onAnimationEnd={onAnimationEnd}
              onPhaseTwo={handlePhaseTwo}
              images={animationImages}
            />
          </Animated.View>
        )}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {state === 'loading' && renderLoadingState()}
      {state === 'error' && renderErrorState()}
      {state === 'empty' && renderEmptyState()}
      {state === 'noInternet' && renderNoInternetState()}
      {state === 'result' && renderResultState()}
    </View>
  );
};

export type SocialFeedPageV4Ref = {
  maybeLoadMoreOnScroll: () => void;
};

export const SocialFeedPageV4 = forwardRef<
  SocialFeedPageV4Ref,
  SocialFeedPageProps
>(
  (
    {
      runFeedAnimation,
      onFeedItemClick,
      onFooterChipClick,
      onRetry,
      onEmptyState,
      onNoInternetState,
      onResultState,
      onLoadingState,
      onAnimationStart,
      onAnimationEnd,
      showCategoryChips = true,
    }: SocialFeedPageProps,
    ref,
  ) => {
    const { recommendationHashKey } = useSocialFeedPageContext();
    const { config } = useAppConfig();
    const { trackFeedItemClick, trackApiError } = useSocialAnalytics();
    const { height: windowHeight } = useWindowDimensions();

    const handleFeedItemClick = useCallback(
      (post: Post, index: number) => {
        trackFeedItemClick({ post, currency: config.currency ?? '', index });
        onFeedItemClick?.(post, index);
      },
      [onFeedItemClick, config.currency, trackFeedItemClick],
    );
    const movementsHomeQuery = useSocialFeed();

    const shouldRunAnimation = runFeedAnimation;

    const [animationDone, setAnimationDone] = useState(false);

    const [revealData, setRevealData] = useState<SocialFeedData | undefined>(
      undefined,
    );
    const lastTrackedErrorRef = useRef<{ label: string } | null>(null);
    const endReachedSentinelRef = useRef<View>(null);
    const isCheckingEndReachedRef = useRef(false);
    const autoLoadInFlightRef = useRef(false);

    useEffect(() => {
      if (!movementsHomeQuery.isError || !movementsHomeQuery.error) return;
      const { code, message, description } = parseApiError(
        movementsHomeQuery.error,
      );
      const label = `${code}|${message}|${config.locale ?? ''}|${config.geoId ?? ''}`;
      if (lastTrackedErrorRef.current?.label === label) return;
      lastTrackedErrorRef.current = { label };
      trackApiError({
        code,
        category: 'feed',
        description,
        functionality: 'fetch movements home',
        label,
      });
    }, [
      movementsHomeQuery.isError,
      movementsHomeQuery.error,
      config.locale,
      config.geoId,
      trackApiError,
    ]);

    useEffect(() => {
      if (!shouldRunAnimation) return;
      if (!revealData) return;
      if (animationDone) return;

      onAnimationStart?.();
    }, [animationDone, revealData, shouldRunAnimation, onAnimationStart]);

    const handleAnimationEnd = useCallback(() => {
      setAnimationDone(true);
      onAnimationEnd?.();
    }, [onAnimationEnd]);

    const flattenedData = useMemo(() => {
      if (!movementsHomeQuery.data || !movementsHomeQuery.data.pages)
        return undefined;

      return {
        ...movementsHomeQuery.data.pages[0],
        data: movementsHomeQuery.data.pages.flatMap((page) => page.data || []),
      };
    }, [movementsHomeQuery.data]);

    useEffect(() => {
      if (!flattenedData) return;
      if (
        shouldRunAnimation &&
        !animationDone &&
        movementsHomeQuery.isFetching
      ) {
        return;
      }
      setRevealData(flattenedData);
    }, [
      flattenedData,
      shouldRunAnimation,
      animationDone,
      movementsHomeQuery.isFetching,
    ]);

    const derivedState: SocialFeedState = useMemo(() => {
      const {
        data: queryData,
        isError,
        error,
        isPending,
        isFetching,
        isLoading,
      } = movementsHomeQuery;

      if (revealData) {
        if (!revealData.data?.length) return 'empty';
        return 'result';
      }

      if (isError) {
        const { code } = parseApiError(error);
        return code === 'NETWORK_ERROR' ? 'noInternet' : 'error';
      }

      if (queryData) {
        return 'loading';
      }

      // Only show loading if we don't have animation to mask it
      if (!shouldRunAnimation && (isPending || isFetching || isLoading)) {
        return 'loading';
      }

      return 'result';
    }, [
      revealData,
      movementsHomeQuery.isError,
      movementsHomeQuery.error,
      movementsHomeQuery.data,
      movementsHomeQuery.isPending,
      movementsHomeQuery.isFetching,
      movementsHomeQuery.isLoading,
      shouldRunAnimation,
    ]);

    const handleRetry = async () => {
      await movementsHomeQuery.refetch();
      onRetry?.();
    };

    const maybeLoadMoreOnScroll = useCallback(() => {
      if (
        !movementsHomeQuery.hasNextPage ||
        movementsHomeQuery.isFetchingNextPage ||
        movementsHomeQuery.isError ||
        autoLoadInFlightRef.current ||
        isCheckingEndReachedRef.current
      ) {
        return;
      }

      const sentinel = endReachedSentinelRef.current;
      if (!sentinel) {
        return;
      }

      isCheckingEndReachedRef.current = true;
      sentinel.measureInWindow((_x, y) => {
        isCheckingEndReachedRef.current = false;

        const threshold = 220;
        const isNearViewportBottom =
          y <= windowHeight + threshold && y >= -threshold;

        if (isNearViewportBottom) {
          autoLoadInFlightRef.current = true;
          void movementsHomeQuery.fetchNextPage().finally(() => {
            autoLoadInFlightRef.current = false;
          });
        }
      });
    }, [
      movementsHomeQuery.hasNextPage,
      movementsHomeQuery.isFetchingNextPage,
      movementsHomeQuery.isError,
      movementsHomeQuery.fetchNextPage,
      windowHeight,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        maybeLoadMoreOnScroll,
      }),
      [maybeLoadMoreOnScroll],
    );

    return (
      <>
        <SocialFeedPageView
          recommendationHashKey={recommendationHashKey}
          runFeedAnimation={shouldRunAnimation}
          animationDone={animationDone}
          onAnimationEnd={handleAnimationEnd}
          state={derivedState}
          data={revealData}
          onFeedItemClick={handleFeedItemClick}
          onFooterChipClick={onFooterChipClick}
          onRetry={handleRetry}
          onEmptyState={onEmptyState}
          onNoInternetState={onNoInternetState}
          onResultState={onResultState}
          onLoadingState={onLoadingState}
          onLoadMore={movementsHomeQuery.fetchNextPage}
          isLoadingMore={movementsHomeQuery.isFetchingNextPage}
          hasNextPage={movementsHomeQuery.hasNextPage}
          showCategoryChips={showCategoryChips}
        />
        {/* Sentinel for scroll-to-bottom pagination detection. */}
        <View ref={endReachedSentinelRef} />
      </>
    );
  },
);

SocialFeedPageV4.displayName = 'SocialFeedPageV4';

const styles = StyleSheet.create((pd) => ({
  container: {
    flex: 1,
    paddingBottom: pd.ref.space.spacing40.px,
  },
  // Wraps FeedListWidget only — deliberately has no paddingBottom of its own.
  // The outer page `container` above already adds bottom spacing once; reusing
  // `container` here duplicated it, pushing the loaded grid's bottom padding
  // well past the skeleton's.
  feedWrapper: {
    flex: 1,
  },
  animationWrapper: {
    flex: 1,
    position: 'relative' as const,
  },
  absoluteFill: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  animationOverlay: {
    position: 'absolute' as const,
    top: ANIMATION_OVERLAY_TOP_OFFSET,
    left: 0,
    right: 0,
    bottom: 0,
  },
  chipsContainer: {
    marginTop: 8,
    marginBottom: 8,
  },
  chipsContent: {
    paddingHorizontal: 16,
  },
  chipWrapper: {
    marginRight: 8,
  },
}));
